import { useState, useEffect, useCallback, useRef } from 'react';

export const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeToasts, setActiveToasts] = useState([]);
  const previousUnreadCountRef = useRef(0);
  const notificationIdsRef = useRef(new Set());
  const toastIdCounterRef = useRef(0);

  const removeToast = (toastId) => {
    // Find the toast element
    const toastElement = document.querySelector(`[data-toast-id="${toastId}"]`);
    
    if (toastElement) {
      // Add the closing class to trigger the fade-out animation
      toastElement.classList.add('closing');
      
      // Wait for animation to complete before removing from state
      setTimeout(() => {
        setActiveToasts(prev => prev.filter(toast => toast.toastId !== toastId));
      }, 500); // Match this with the CSS animation duration
    } else {
      // If element not found, just remove from state
      setActiveToasts(prev => prev.filter(toast => toast.toastId !== toastId));
    }
  };

  const addToast = (notification) => {
    const toastId = `toast-${toastIdCounterRef.current++}`;
    
    // Add the new toast to the beginning of the array
    setActiveToasts(current => [{
      toastId,
      notification,
      timestamp: new Date()
    }, ...current]);
    
    // Auto-remove toast after 5 seconds
    setTimeout(() => {
      removeToast(toastId);
    }, 5000);
  };

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    
    // Only set loading on initial fetch
    if (notifications.length === 0) {
      setLoading(true);
    }
    
    setError(null);
    
    try {
      const response = await fetch(`https://service9001.szapfs.org/notifications/${userId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Calculate unread count
      const unreadNotifications = data.filter(notification => notification.Status === 'Unread');
      const unread = unreadNotifications.length;
      
      // Get current ids for comparison
      const currentIds = new Set(data.map(n => n.id));
      
      // Find new notifications by comparing with previous ids
      const newNotifications = data.filter(n => {
        return n.Status === 'Unread' && !notificationIdsRef.current.has(n.id);
      });

      // Show toasts for new notifications
      if (newNotifications.length > 0) {
        // Sort new notifications by date (newest first)
        const sortedNewNotifications = newNotifications.sort((a, b) => 
          new Date(b.Date) - new Date(a.Date)
        );
        
        // Add toasts for each new notification
        sortedNewNotifications.forEach(notification => {
          console.log('Adding toast for:', notification);
          addToast(notification);
        });
      }
      
      // Update refs after processing
      previousUnreadCountRef.current = unread;
      notificationIdsRef.current = currentIds;
      
      // Update state
      setNotifications(data);
      setUnreadCount(unread);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, notifications.length]);

  const markAsRead = useCallback(async (notificationId) => {
    if (!userId || !notificationId) return;
    
    try {
      const now = new Date().toISOString();
      const response = await fetch(`https://service9001.szapfs.org/notifications/read/${notificationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Status: 'Read',
          ReadDate: now
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to mark notification as read: ${response.status}`);
      }
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.id === notificationId 
            ? { ...notification, Status: 'Read', ReadDate: now } 
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [userId]);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    
    try {
      const now = new Date().toISOString();
      const response = await fetch(`https://service9001.szapfs.org/notifications/read-all/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Status: 'Read',
          ReadDate: now
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to mark all notifications as read: ${response.status}`);
      }
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.Status === 'Unread' 
            ? { ...notification, Status: 'Read', ReadDate: now } 
            : notification
        )
      );
      
      // Reset unread count
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [userId]);

  const refreshNotifications = useCallback(() => {
    // Clear all active toasts
    setActiveToasts([]);
    fetchNotifications();
  }, [fetchNotifications]);

  // Run on first render to establish initial notification IDs
  useEffect(() => {
    if (notifications.length > 0 && notificationIdsRef.current.size === 0) {
      notificationIdsRef.current = new Set(notifications.map(n => n.id));
    }
  }, [notifications]);

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications();
    
    // Set up polling every 3.5 seconds
    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 3500);
    
    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    activeToasts,
    removeToast
  };
};
